/**
 * LockedFormInput Component
 * Form input that can be locked/disabled with a lock icon and tooltip
 */

import React, { forwardRef } from 'react';
import { theme } from '../../theme';
import { FormInput, FormInputProps } from './FormInput';
import { LockIcon } from '../feedback/LockIcon';

export interface LockedFormInputProps extends FormInputProps {
  /** Whether the field is locked (non-editable due to permissions) */
  locked?: boolean;
  /** Tooltip text explaining why the field is locked */
  lockedTooltip?: string;
  /** Position of the lock icon */
  lockIconPosition?: 'label' | 'input';
}

/**
 * Form input with lock state support for permission-based field disabling
 *
 * @example
 * ```tsx
 * <LockedFormInput
 *   label="Labor Hours"
 *   value={laborHours}
 *   locked={!canEditPricing}
 *   lockedTooltip="You don't have permission to edit pricing at this stage"
 *   onChange={(e) => setLaborHours(e.target.value)}
 * />
 * ```
 */
export const LockedFormInput = forwardRef<HTMLInputElement, LockedFormInputProps>(
  ({ locked = false, lockedTooltip, lockIconPosition = 'label', label, style, disabled, ...props }, ref) => {
    // When locked, the field should be disabled
    const isDisabled = locked || disabled;

    // Lock icon to show next to the label
    const lockIconElement = locked && (
      <LockIcon size={14} color={theme.colors.text.tertiary} tooltip={lockedTooltip} tooltipPosition="top" style={{ marginLeft: 6 }} />
    );

    // Custom label with lock icon
    const enhancedLabel =
      label && lockIconPosition === 'label' ? (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {label}
          {lockIconElement}
        </span>
      ) : (
        label
      );

    // Locked input styling
    const lockedStyles: React.CSSProperties = locked
      ? {
          backgroundColor: theme.colors.background.tertiary,
          cursor: 'not-allowed',
          opacity: 0.7,
          borderColor: theme.colors.border.light,
        }
      : {};

    // Container style for input with lock icon
    const containerStyle: React.CSSProperties = {
      position: 'relative',
      ...style,
    };

    return (
      <div style={containerStyle}>
        <FormInput
          ref={ref}
          label={enhancedLabel as string | undefined}
          disabled={isDisabled}
          style={{
            ...lockedStyles,
            ...(lockIconPosition === 'input' && locked ? { paddingRight: 36 } : {}),
          }}
          {...props}
        />
        {locked && lockIconPosition === 'input' && (
          <div
            style={{
              position: 'absolute',
              right: 12,
              top: label ? '50%' : '50%',
              transform: label ? 'translateY(calc(-50% + 10px))' : 'translateY(-50%)',
              pointerEvents: 'auto',
            }}
          >
            <LockIcon size={16} color={theme.colors.text.tertiary} tooltip={lockedTooltip} tooltipPosition="left" />
          </div>
        )}
      </div>
    );
  }
);

LockedFormInput.displayName = 'LockedFormInput';

export default LockedFormInput;
